import React, { createContext, useState, useEffect, useContext, useRef } from 'react';

// Create a context for location data
export const LocationContext = createContext();

// Global flag to prevent multiple logs and errors in console
let originalConsoleError;

// Override console.error to filter CoreLocationProvider messages
if (typeof window !== 'undefined' && window.console) {
  originalConsoleError = console.error;
  console.error = function(...args) {
    // Suppress CoreLocationProvider error messages
    if (args[0] && typeof args[0] === 'string' && 
        args[0].includes('CoreLocationProvider')) {
      return; // Don't log these errors
    }
    
    // Log all other errors normally
    originalConsoleError.apply(console, args);
  };
}

export const LocationProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isWatching, setIsWatching] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
  const [locationRetries, setLocationRetries] = useState(0);
  const [maxRetriesReached, setMaxRetriesReached] = useState(false);
  const mountedRef = useRef(false);

  // Function to get the user's current position once
  const getCurrentPosition = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    // Get refresh interval from localStorage (default 30 seconds)
    // Using a higher default for better performance especially in development
    const refreshInterval = parseInt(localStorage.getItem('locationRefreshInterval')) || 30;
    
    // Detect if we're in development mode and use an even higher interval
    const isDevelopment = process.env.NODE_ENV === 'development';
    const effectiveInterval = isDevelopment ? Math.max(refreshInterval, 60) : refreshInterval;
    
    const options = {
      enableHighAccuracy: true, // Higher accuracy for live tracking
      timeout: 10000, // 10 seconds
      maximumAge: effectiveInterval * 1000 // Use effective interval (higher in dev)
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed, // meters per second
          heading: position.coords.heading, // degrees from north
          timestamp: position.timestamp
        };
        setUserLocation(pos);
        setLastLocationUpdate(Date.now());
        setLocationError(null);
      },
      (error) => {
        handleLocationError(error);
      },
      options
    );
  };

  // Default location (San Jose, CA as fallback)
  const defaultLocation = {
    lat: 37.3382,
    lng: -121.8863,
    accuracy: 1000,
    timestamp: Date.now(),
    isDefault: true
  };

  // Handle geolocation errors with retry limiting
  const handleLocationError = (error, shouldUseDefault = true) => {
    let errorMessage = '';
    let isCritical = false;
    
    switch(error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location access denied. Using default location.';
        shouldUseDefault = true;
        isCritical = true; // Stop retrying if permission denied
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'GPS unavailable. Using default location.';
        shouldUseDefault = true;
        break;
      case error.TIMEOUT:
        errorMessage = 'Location timeout. Using default location.';
        shouldUseDefault = true;
        break;
      default:
        errorMessage = `Location error. Using default location.`;
        shouldUseDefault = true;
    }
    
    // Increment retry counter
    setLocationRetries(prev => prev + 1);
    
    // Stop aggressive retrying after 3 attempts or critical error
    if (locationRetries >= 2 || isCritical) {
      setMaxRetriesReached(true);
      setLocationError(errorMessage);
      
      // Stop watching if active
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
        setWatchId(null);
        setIsWatching(false);
      }
    }
    
    // Use default location as fallback when geolocation fails
    if (shouldUseDefault && !userLocation) {
      setUserLocation({ ...defaultLocation, isDefault: true });
      setLastLocationUpdate(Date.now());
    }
  };

  // Function to start watching the user's location
  const startWatchingLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      setUserLocation({ ...defaultLocation, isDefault: true });
      setMaxRetriesReached(true);
      return;
    }

    if (isWatching || maxRetriesReached) return; // Already watching or max retries reached

    try {
      // First get current position immediately
      getCurrentPosition();
      
      // Get refresh interval from localStorage (default 30 seconds)
      const refreshInterval = parseInt(localStorage.getItem('locationRefreshInterval')) || 30;
      
      // Use higher interval in development mode
      const isDevelopment = process.env.NODE_ENV === 'development';
      const effectiveInterval = isDevelopment ? Math.max(refreshInterval, 60) : refreshInterval;
      
      // Options for the geolocation API - optimized for live tracking
      const options = {
        enableHighAccuracy: true, // Higher accuracy for live tracking
        timeout: 10000, // 10 seconds
        maximumAge: effectiveInterval * 1000, // Use effective interval (higher in dev)
        // Reduce precision in development to improve performance
        ...(isDevelopment ? { enableHighAccuracy: false } : {})
      };

      // Start watching position
      const id = navigator.geolocation.watchPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            speed: position.coords.speed, // meters per second
            heading: position.coords.heading, // degrees from north
            timestamp: position.timestamp
          };
          setUserLocation(pos);
          setLastLocationUpdate(Date.now());
          setLocationError(null);
        },
        (error) => {
          handleLocationError(error);
          
          // Only try getCurrentPosition once as fallback
          if (!userLocation && locationRetries === 0) {
            getCurrentPosition();
          }
        },
        options
      );

      setWatchId(id);
      setIsWatching(true);
    } catch (error) {
      console.error('Error setting up location tracking:', error);
      setLocationError('Location tracking unavailable');
      setUserLocation({ ...defaultLocation, isDefault: true });
      setMaxRetriesReached(true);
    }
  };

  // Function to stop watching the user's location
  const stopWatchingLocation = () => {
    if (watchId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
      setIsWatching(false);
    }
  };

  // Function to manually refresh location
  const refreshLocation = () => {
    // If we're already watching, stop first
    if (isWatching) {
      stopWatchingLocation();
    }
    
    // Clear any previous errors
    setLocationError(null);
    
    // Start watching again
    setTimeout(() => {
      startWatchingLocation();
    }, 500);
  };

  // Get initial location when component mounts (ONLY ONCE)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      startWatchingLocation();
    }

    // Clean up when component unmounts
    return () => {
      stopWatchingLocation();
    };
    // Empty dependency array - run only on mount/unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Separate effect for periodic location check
  useEffect(() => {
    // Determine check frequency - less aggressive in development
    const isDevelopment = process.env.NODE_ENV === 'development';
    const checkFrequency = isDevelopment ? 120000 : 60000; // 2 min in dev, 1 min in prod
    
    // Set up a periodic check to ensure location is being updated (only if not using default)
    const locationCheckInterval = setInterval(() => {
      if (isWatching && lastLocationUpdate && !maxRetriesReached) {
        const timeSinceLastUpdate = Date.now() - lastLocationUpdate;
        // If no update in a while, try to refresh (more lenient in dev)
        const refreshThreshold = isDevelopment ? 180000 : 60000; // 3 min in dev, 1 min in prod
        if (timeSinceLastUpdate > refreshThreshold) {
          refreshLocation();
        }
      }
    }, checkFrequency); // Check less frequently in development

    // Clean up interval
    return () => {
      clearInterval(locationCheckInterval);
    };
    // Only re-run if these specific values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWatching, lastLocationUpdate, maxRetriesReached]);

  return (
    <LocationContext.Provider 
      value={{ 
        userLocation, 
        locationError, 
        isWatching,
        startWatchingLocation,
        stopWatchingLocation,
        refreshLocation
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook to use the location context
export const useLocation = () => useContext(LocationContext);
